import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useAuth } from "./AuthContext";
import {
  getPaymentStatus,
  createOrder,
  verifyPayment,
  openRazorpayCheckout,
} from "../services/payment";
import { setUpgradeHandler } from "../services/api";

const PaymentContext = createContext(null);

export function usePayment() {
  const ctx = useContext(PaymentContext);
  if (!ctx) throw new Error("usePayment must be used within PaymentProvider");
  return ctx;
}

export function PaymentProvider({ children }) {
  const { currentUser } = useAuth();

  // ── plan / credits state ──────────────────────────────────────────
  const [status, setStatus] = useState(null); // { plan, credits, ... }
  const [statusLoading, setStatusLoading] = useState(false);

  // ── upgrade modal state ───────────────────────────────────────────
  const [upgradeData, setUpgradeData] = useState(null); // from 429
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null); // { success, message }

  // ── fetch current credit / plan status ────────────────────────────
  const fetchStatus = useCallback(async () => {
    if (!currentUser) {
      setStatus(null);
      return;
    }
    try {
      setStatusLoading(true);
      const res = await getPaymentStatus();
      setStatus(res.data);
    } catch {
      // non-critical — badge just won't show
    } finally {
      setStatusLoading(false);
    }
  }, [currentUser]);

  // Auto-fetch when auth state changes
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // ── upgrade trigger (called from 429 interceptor) ─────────────────
  const triggerUpgrade = useCallback((data) => {
    setUpgradeData(data);
    setShowUpgradeModal(true);
    setPaymentResult(null);
  }, []);

  // Register the handler so the Axios interceptor can call it
  useEffect(() => {
    setUpgradeHandler(triggerUpgrade);
    return () => setUpgradeHandler(null);
  }, [triggerUpgrade]);

  const dismissUpgrade = useCallback(() => {
    setShowUpgradeModal(false);
    setPaymentResult(null);
  }, []);

  // ── payment flow ──────────────────────────────────────────────────
  const handlePayment = useCallback(
    async (planId) => {
      if (!currentUser) return;

      // Razorpay checkout.js cannot run inside a Chrome extension popup (CSP).
      // Redirect the user to the web app to complete the upgrade instead.
      if (process.env.REACT_APP_EXTENSION === "true") {
        const webUrl = process.env.REACT_APP_WEB_URL || "https://referralcopilot.in";
        window.open(`${webUrl}/?upgrade=1`, "_blank");
        return;
      }

      try {
        setPaymentProcessing(true);
        setPaymentResult(null);

        // 1. Create Razorpay order on backend
        const { data } = await createOrder(planId);

        // 2. Open Razorpay checkout modal
        const rzpResponse = await openRazorpayCheckout({
          order: data.order,
          keyId: data.keyId,
          user: currentUser,
        });

        // 3. Verify payment on backend
        const { data: verification } = await verifyPayment({
          razorpay_order_id: rzpResponse.razorpay_order_id,
          razorpay_payment_id: rzpResponse.razorpay_payment_id,
          razorpay_signature: rzpResponse.razorpay_signature,
        });

        setPaymentResult({ success: true, message: verification.message || "Payment successful!" });

        // Refresh credits / plan status
        await fetchStatus();
      } catch (err) {
        const msg =
          err.message === "Payment cancelled"
            ? "Payment was cancelled."
            : err.response?.data?.error || err.message || "Payment failed.";
        setPaymentResult({ success: false, message: msg });
      } finally {
        setPaymentProcessing(false);
      }
    },
    [currentUser, fetchStatus]
  );

  const value = {
    status,
    statusLoading,
    upgradeData,
    showUpgradeModal,
    paymentProcessing,
    paymentResult,
    fetchStatus,
    triggerUpgrade,
    dismissUpgrade,
    handlePayment,
  };

  return (
    <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>
  );
}
